import React from 'react';
import { BodyProps, TableProps } from 'new/elements/Table/types';
import styles from 'new/elements/Table/Table.module.scss';
import { Checkbox, IconButton } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { get } from 'lodash';
import { useTableContext } from 'new/elements/Table/useTableContext';
import cx from 'classnames';

function CollapseIcon({ show, opened, onClick, render }) {
  if (!render) {
    return null;
  }
  return (
    <IconButton
      aria-label="expand row"
      className={cx(styles.expand, !show ? styles.expand_hidden : '')}
      onClick={onClick}
    >
      {opened ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
    </IconButton>
  );
}

function Check({ row, onSelectedRowsChange, canSelect }) {
  const { selectedRows, onSelectRow, allRowsSelected, selectableRows } = useTableContext();

  const onChange = () => {
    onSelectRow(row);
  };

  if (!selectableRows.length) {
    return null;
  }

  if (!onSelectedRowsChange) {
    return null;
  }

  return (
    <Checkbox
      disabled={!canSelect}
      className={!canSelect ? styles.hide : ''}
      checked={allRowsSelected || selectedRows.includes(row)}
      onChange={onChange}
    />
  );
}

type TDProps<T> = Partial<TableProps<T>> & BodyProps<T> & TableCellRenderer;
function Td<T>({ column, rowData, getRowTdClassName, accessoryData }: TDProps<T>) {
  const rowTdClassName = getRowTdClassName?.(rowData, column);

  const style = column.hasOwnProperty('getTdStyle')
    ? column.getTdStyle(rowData, column)
    : { flex: '0 1 ' + column.width + '%' };

  return (
    <div
      style={style}
      aria-label="table-td-row"
      className={[
        styles.rowTd,
        column.center ? styles.rowTd_center : '',
        column.numeric ? styles.rowTd_numeric : '',
        rowTdClassName,
        column.tdClassName,
      ].join(' ')}
    >
      {column.renderRow
        ? column.renderRow({ ...rowData, ...accessoryData })
        : get(rowData, column.field)}
    </div>
  );
}

export function Body<T>(props: Partial<TableProps<T>> & BodyProps<T> & TableCellRenderer) {
  const {
    columns,
    rowData,
    className,
    style,
    open,
    getRowTdClassName,
    onClickExpand,
    shouldRowExpand,
    rowCollapsibleRender,
    rowEndRender,
    onRowClick,
    onSelectedRowsChange,
    showCheckbox,
    isRowSelectable,
    isRowExpandable,
    accessoryData,
    collapseOnRowClick,
    disableRowHighlight,
    loading,
    index,
  } = props;

  const canExpandRow =
    isRowExpandable && typeof isRowExpandable === 'function' ? isRowExpandable(rowData) : true;

  const onClick = () => {
    if (onRowClick) {
      onRowClick(rowData);
      return;
    }

    if (collapseOnRowClick && canExpandRow && typeof onClickExpand === 'function') {
      onClickExpand(rowData);
    }
  };

  const onExpandClicked = () => {
    if (canExpandRow) {
      onClickExpand?.(rowData);
    }
  };

  const canSelectRow =
    isRowSelectable && typeof isRowSelectable === 'function' ? isRowSelectable(rowData) : true;

  const clickable = !!onRowClick || (collapseOnRowClick && canExpandRow);

  const isRowOpened =
    typeof shouldRowExpand === 'function' ? shouldRowExpand(open, rowData) : open === rowData;

  const mainContent = (showCollapseIcon = false) => {
    return (
      <div
        onClick={clickable ? onClick : undefined}
        className={[
          'table_rowClassname',
          styles.row,
          disableRowHighlight ? styles.row_disable_highlighting : '',
          clickable ? styles.row_clickable : '',
          className,
        ].join(' ')}
      >
        {showCheckbox && (
          <Check
            canSelect={canSelectRow}
            row={rowData}
            onSelectedRowsChange={onSelectedRowsChange}
          />
        )}
        <CollapseIcon
          show={showCollapseIcon}
          render={typeof rowCollapsibleRender === 'function'}
          opened={isRowOpened}
          onClick={onExpandClicked}
        />
        {columns.map((column, columnIndex) => {
          return (
            <Td
              key={columnIndex}
              getRowTdClassName={getRowTdClassName}
              column={column}
              rowData={rowData}
              accessoryData={accessoryData}
            />
          );
        })}
        {rowEndRender ? (
          <div className={styles.rowEndRenderContainer}>
            {rowEndRender({ ...rowData, ...accessoryData })}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      style={{ ...style, height: undefined }}
      className={cx(
        styles.rowContainer,
        !!rowCollapsibleRender && open === index ? styles.rowContainerBorderBottom : '',
      )}
    >
      {mainContent(canExpandRow)}
      {!!rowCollapsibleRender &&
      (typeof shouldRowExpand === 'function' ? shouldRowExpand(open, rowData) : open === rowData) &&
      !loading ? (
        <div>{rowCollapsibleRender(rowData)}</div>
      ) : null}
    </div>
  );
}
